import { ArrowDown, ArrowRight, Route } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { CardSkeleton } from "@/components/Loading";
import type { TraceChain, TraceResponse, TraceReverse } from "../../shared/api";
import { TRACE_FIFO_NOTE, missingReceiptText, traceStatusText, wasteText } from "../../shared/trace";
import { formatKg, formatNumber } from "../../shared/versions";
import { StatusBadge } from "@/components/StatusBadge";
import { ErrorAlert } from "@/components/ErrorAlert";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { SegmentBar } from "@/components/charts";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { screens } from "../screens";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorText, getTrace, getTracePdf } from "@/lib/client";

/** API dates are ISO (yyyy-mm-dd); the screen shows GG.AA.YYYY. */
export const fmtDate = (iso: string) => iso.slice(0, 10).split("-").reverse().join(".");
const kg = (n: number) => `${formatKg(n)} kg`;
const unique = (a: string[]) => a.filter((f, i) => a.indexOf(f) === i);

/** "Elle girildi" (ui.md §11.5): tek kayıt satırının sonunda, toplam gösteren kutuda "Kaynak:" satırının sonunda. */
const Manual = () => <StatusBadge tone="info" className="ml-2">Elle girildi</StatusBadge>;

function Box({ title, file, children, step, manual, broken, last }: { title: string; file: string[]; children: ReactNode; step: number; manual?: boolean; broken?: boolean; last?: boolean }) {
  return (
    <li className="flex min-w-0 flex-1 flex-col items-stretch gap-2 2xl:flex-row">
      <Card className={cn("min-w-0 flex-1 text-sm tabular-nums", broken && "border-critical-border")}>
        <h3 className="flex items-center gap-2 font-medium">
          <span aria-hidden="true" className={cn("flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs text-primary", broken && "bg-critical text-critical-foreground")}>{step}</span>{title}
        </h3>
        <div className="mt-1 flex flex-col gap-1 break-words">{children}</div>
        <div className="mt-2 text-xs text-muted-foreground">
          <p>Kaynak:</p>
          {file.length ? file.flatMap((f) => f.split(", ")).map((f, i, all) => <p key={f} className="break-all">{f}{manual && i === all.length - 1 && <Manual />}</p>) : <p>—{manual && <Manual />}</p>}
        </div>
      </Card>
      {!last && (<>
        <ArrowDown aria-hidden="true" className="size-4 shrink-0 self-center text-muted-foreground 2xl:hidden" />
        <ArrowRight aria-hidden="true" className="hidden size-4 shrink-0 self-center text-muted-foreground 2xl:block" />
      </>)}
    </li>
  );
}

const Boxes = ({ label, children }: { label: string; children: ReactNode }) => (
  <ol aria-label={label} className="flex flex-col gap-2 2xl:flex-row">{children}</ol>
);

/** Zincir durumu: cümle spec'ten (traceStatusText), "Tam" ve "Kopuk" yalnız TraceStatus.completeKg / brokenKg. Toplam ya da oran hesaplanmaz. */
function Footer({ status }: { status: TraceChain["traceStatus"] }) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="font-medium">{traceStatusText(status)}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {([["Tam", status.completeKg], ["Kopuk", status.brokenKg]] as const).map(([label, v]) => (
          <div key={label} className="rounded-lg border bg-card p-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-xl font-semibold tabular-nums">{kg(v)}</p>
          </div>
        ))}
      </div>
      <SegmentBar segments={[{ label: "Tam", count: status.completeKg, color: "primary" as const }, { label: "Kopuk", count: status.brokenKg, color: "critical" as const }].filter((s) => s.count > 0)} hideCounts />
      <p className="text-muted-foreground">{TRACE_FIFO_NOTE}</p>
    </div>
  );
}

/** Beyanname → Depo girişi → İş emirleri → Üretim → İhracat (spec §6 E4). */
export function ChainView({ c }: { c: TraceChain }) {
  const d = c.declaration, r = c.warehouseReceipt, w = c.workOrders, p = c.production;
  return (
    <section aria-label={`Zincir ${d.declarationNo}`} className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{d.declarationNo}</h2>
      <Boxes label="İzleme zinciri kutuları">
        <Box step={1} title="Beyanname" file={[d.file]}>
          <p>{d.declarationNo} · {fmtDate(d.date)}{d.manualEntryId !== null && <Manual />}</p>
          <p>Menşe {d.originCountry ?? "—"} · GTİP {d.hsCode}</p>
          <p>{kg(d.quantityKg)}{d.ipaNo ? ` · DİİB ${d.ipaNo}` : ""}</p>
        </Box>
        <Box step={2} title="Depo girişi" file={[r.file]} manual={r.hasManualEntry} broken={r.missingKg > 0}>
          <p>{kg(r.quantityKg)} · {formatNumber(r.movements.length)} hareket</p>
          {r.missingKg > 0 && <p className="font-medium text-critical-foreground">⚠ {missingReceiptText(r.missingKg)}</p>}
        </Box>
        <Box step={3} title="İş emirleri" file={[w.file]} manual={w.hasManualEntry}>
          <p>{formatNumber(w.count)} iş emri{w.firstDate && w.lastDate ? ` · ${fmtDate(w.firstDate)} – ${fmtDate(w.lastDate)}` : ""}</p>
          <p>Tahsis edilen: {kg(w.allocatedKg)}</p>
        </Box>
        <Box step={4} title="Üretim" file={p ? p.files : []} manual={p?.hasManualEntry}>
          {p ? (<><p>{p.material}</p><p>{kg(p.productKg)}</p><p>{wasteText(p.wastePct)}</p></>) : <p className="text-muted-foreground">Reçete satırı yok.</p>}
        </Box>
        <Box step={5} last title="İhracat" file={unique(c.exports.map((e) => e.file))}>
          {c.exports.length === 0 ? <p className="text-muted-foreground">İhracat kaydı yok.</p> : c.exports.map((e) => (
            <p key={e.declarationNo}>{e.declarationNo} · {fmtDate(e.date)} · {e.country ?? "—"} · {kg(e.allocatedKg)}{e.manualEntryId !== null && <Manual />}</p>
          ))}
        </Box>
      </Boxes>
      <Footer status={c.traceStatus} />
    </section>
  );
}

/** Tersten arama: ihracat beyannamesinden geriye, aynı FIFO tahsisi. */
export function ReverseView({ r }: { r: TraceReverse }) {
  const d = r.declaration;
  return (
    <section aria-label={`Tersten zincir ${d.declarationNo}`} className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{d.declarationNo} (ihracat)</h2>
      <Boxes label="Tersten izleme kutuları">
        <Box step={1} title="İhracat" file={[d.file]}>
          <p>{d.declarationNo} · {fmtDate(d.date)}{d.manualEntryId !== null && <Manual />}</p>
          <p>{d.country ?? "—"} · {kg(d.quantityKg)}</p>
        </Box>
        <Box step={2} last title="Kaynak girişler" file={unique(r.sources.map((s) => s.file))}>
          {r.sources.length === 0 ? <p className="text-muted-foreground">Kaynak giriş yok.</p> : r.sources.map((s) => (
            <p key={`${s.declarationNo}|${s.movementNo}`}>{s.declarationNo ?? "Beyannamesiz"} · {s.movementNo} · {kg(s.allocatedKg)}{s.manualEntryId !== null && <Manual />}</p>
          ))}
        </Box>
      </Boxes>
      <Footer status={r.traceStatus} />
    </section>
  );
}

export function TraceResult({ t }: { t: TraceResponse }) {
  if (t.chains.length === 0 && t.reverse.length === 0) return <p className="text-sm text-muted-foreground">Bu numara için zincir bulunamadı.</p>;
  return (
    <div className="flex flex-col gap-8">
      {t.chains.map((c) => <ChainView key={c.declaration.declarationNo} c={c} />)}
      {t.reverse.map((r) => <ReverseView key={r.declaration.declarationNo} r={r} />)}
    </div>
  );
}

/** E4 (spec §6). Salt okunur; arama view izniyle (API denetler). */
export function TraceScreen() {
  const [ref, setRef] = useState("");
  const [searched, setSearched] = useState(""); // son aranan değer; PDF kutudaki güncel yazıyı değil bunu kullanır
  const [result, setResult] = useState<TraceResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const seq = useRef(0); // yalnız son aramanın yanıtı uygulanır (hızlı geri/ileri)
  const search = async (r: string) => {
    const mine = ++seq.current;
    setSearched(r); setBusy(true); setError(""); setResult(null);
    try { const t = await getTrace(r); if (mine === seq.current) setResult(t); }
    catch (err) { if (mine === seq.current) { setResult(null); setError(errorText(err)); } }
    finally { if (mine === seq.current) setBusy(false); }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const r = ref.trim();
    if (new URLSearchParams(window.location.search).get("ref") !== r) window.history.pushState(null, "", `${window.location.pathname}?${new URLSearchParams({ ref: r })}`); // paylaşılabilir; geri tuşu önceki aramaya döner
    void search(r);
  };
  useEffect(() => { // Panodan "İzle →" (?ref=), paylaşılan bağlantı ve geri/ileri: numara kutuya yazılır, arama çalışır
    const sync = () => {
      const r = new URLSearchParams(window.location.search).get("ref")?.trim() ?? "";
      setRef(r);
      if (r) void search(r); else { seq.current++; setSearched(""); setResult(null); setError(""); setBusy(false); }
    };
    sync();
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);
  async function download() {
    setError(""); setDownloading(true);
    try {
      const { blob, fileName } = await getTracePdf(searched);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: fileName }).click();
      URL.revokeObjectURL(url);
    } catch (err) { setError(errorText(err)); } finally { setDownloading(false); }
  }
  return (
    <main className="flex flex-col gap-6 p-6">
      <PageHeader title="İzleme Zinciri" description={screens.find((x) => x.id === "E4")!.description} />
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="trace-ref">Beyanname, hareket ya da iş emri no</Label>
          <Input id="trace-ref" className="w-80" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="ör. IM-2026/0001" />
        </div>
        <Button type="submit" disabled={busy || !ref.trim()}>{busy ? "Aranıyor…" : "Ara"}</Button>
        {result && (result.chains.length > 0 || result.reverse.length > 0) && <Button type="button" variant="outline" disabled={downloading} onClick={() => void download()}>{downloading ? "Hazırlanıyor…" : "PDF olarak al"}</Button>}
      </form>
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {busy ? <div aria-busy="true"><CardSkeleton delay={0} /></div> : result ? <TraceResult t={result} /> : !error && (
        <Card><EmptyState icon={Route} title="Bir numarayla arayın">Beyanname, hareket ya da iş emri no girin.</EmptyState></Card>
      )}
    </main>
  );
}
